import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Calendar, MessageCircle, FileText, CreditCard, 
  User, LogOut, Clock, CheckCircle, XCircle, DollarSign,
  ArrowRight, Send, Wallet, Star, Edit3
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import DashboardLayout from "../components/DashboardLayout";
import { API, useAuth } from "../App";
import { toast } from "sonner";

const CustomerDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [bookings, setBookings] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedBookingForPayment, setSelectedBookingForPayment] = useState(null);
  const [selectedPaymentType, setSelectedPaymentType] = useState("deposit");
  
  // Review state
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [selectedBookingForReview, setSelectedBookingForReview] = useState(null);
  const [reviewForm, setReviewForm] = useState({ rating: 5, title: "", comment: "" });
  const [submittingReview, setSubmittingReview] = useState(false);

  const activeTab = searchParams.get("tab") || "bookings";

  useEffect(() => {
    fetchBookings();
    fetchConversations();
    
    // Check for payment status
    const paymentStatus = searchParams.get("payment");
    if (paymentStatus === "success") {
      toast.success("Payment successful!");
      pollPaymentStatus();
    } else if (paymentStatus === "cancelled") {
      toast.info("Payment cancelled");
    }
  }, []);

  const pollPaymentStatus = async () => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) return;

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`, {
        withCredentials: true
      });
      if (response.data.payment_status === "paid") {
        fetchBookings();
      }
    } catch (error) {
      console.error("Error checking payment:", error);
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings`, { withCredentials: true });
      setBookings(response.data);
    } catch (error) {
      console.error("Error fetching bookings:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/messages/conversations`, { withCredentials: true });
      setConversations(response.data);
    } catch (error) {
      console.error("Error fetching conversations:", error);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const response = await axios.get(`${API}/messages/${conversationId}`, { withCredentials: true });
      setMessages(response.data);
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  const handleConversationSelect = (conv) => {
    setSelectedConversation(conv);
    fetchMessages(conv.conversation_id);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    try {
      await axios.post(`${API}/messages`, {
        receiver_id: selectedConversation.other_user.user_id,
        content: newMessage
      }, { withCredentials: true });
      
      setNewMessage("");
      fetchMessages(selectedConversation.conversation_id);
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message");
    }
  };

  const handlePayment = async (bookingId, paymentType, method = "stripe") => {
    try {
      if (method === "stripe") {
        const response = await axios.post(
          `${API}/payments/checkout?booking_id=${bookingId}&payment_type=${paymentType}`,
          { origin_url: window.location.origin },
          { withCredentials: true }
        );
        window.location.href = response.data.checkout_url;
      } else if (method === "paypal") {
        const response = await axios.post(
          `${API}/payments/paypal/create-order?booking_id=${bookingId}&payment_type=${paymentType}`,
          { origin_url: window.location.origin },
          { withCredentials: true }
        );
        if (response.data.approval_url) {
          window.location.href = response.data.approval_url;
        } else {
          toast.error("PayPal is not configured");
        }
      }
    } catch (error) {
      console.error("Error creating checkout:", error);
      toast.error(error.response?.data?.detail || "Failed to initiate payment");
    }
    setShowPaymentDialog(false);
  };

  const openPaymentDialog = (booking, paymentType) => {
    setSelectedBookingForPayment(booking);
    setSelectedPaymentType(paymentType);
    setShowPaymentDialog(true);
  };

  // Review functions
  const openReviewDialog = (booking) => {
    setSelectedBookingForReview(booking);
    setReviewForm({ rating: 5, title: "", comment: "" });
    setShowReviewDialog(true);
  };

  const handleSubmitReview = async () => {
    if (!reviewForm.comment || reviewForm.comment.length < 10) {
      toast.error("Please write at least 10 characters in your review");
      return;
    }
    
    setSubmittingReview(true);
    try {
      await axios.post(`${API}/reviews`, {
        booking_id: selectedBookingForReview.booking_id,
        rating: reviewForm.rating,
        title: reviewForm.title || null,
        comment: reviewForm.comment
      }, { withCredentials: true });
      
      toast.success("Review submitted successfully!");
      setShowReviewDialog(false);
      fetchBookings(); // Refresh to show reviewed status
    } catch (error) {
      console.error("Error submitting review:", error);
      toast.error(error.response?.data?.detail || "Failed to submit review");
    } finally {
      setSubmittingReview(false);
    }
  };

  const StarRating = ({ rating, onRate, interactive = true }) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => interactive && onRate(star)}
            className={`${interactive ? 'cursor-pointer hover:scale-110' : 'cursor-default'} transition-transform`}
            disabled={!interactive}
          >
            <Star
              className={`w-6 h-6 ${
                star <= rating
                  ? 'fill-gold text-gold'
                  : 'fill-transparent text-white/30'
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-500/10 text-yellow-500 border-yellow-500/20",
      confirmed: "bg-blue-500/10 text-blue-500 border-blue-500/20",
      completed: "bg-green-500/10 text-green-500 border-green-500/20",
      cancelled: "bg-red-500/10 text-red-500 border-red-500/20"
    };
    return styles[status] || styles.pending;
  };

  const getPaymentBadge = (status) => {
    const styles = {
      unpaid: "bg-red-500/10 text-red-500 border-red-500/20",
      deposit_paid: "bg-yellow-500/10 text-yellow-500 border-yellow-500/20",
      fully_paid: "bg-green-500/10 text-green-500 border-green-500/20"
    };
    return styles[status] || styles.unpaid;
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-white mb-2">
            Welcome back, {user?.name}
          </h1>
          <p className="text-white/60">Manage your bookings and messages</p>
        </div>

        <Tabs value={activeTab} onValueChange={(v) => setSearchParams({ tab: v })}>
          <TabsList className="bg-charcoal border border-white/5 rounded-full p-1 mb-8">
            <TabsTrigger 
              value="bookings" 
              className="rounded-full data-[state=active]:bg-gold data-[state=active]:text-black"
              data-testid="tab-bookings"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Bookings
            </TabsTrigger>
            <TabsTrigger 
              value="messages" 
              className="rounded-full data-[state=active]:bg-gold data-[state=active]:text-black"
              data-testid="tab-messages"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Messages
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bookings">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : bookings.length === 0 ? (
              <div className="text-center py-16 bg-charcoal rounded-2xl border border-white/5">
                <Calendar className="w-12 h-12 text-gold/30 mx-auto mb-4" />
                <h3 className="font-heading text-xl font-bold text-white mb-2">No bookings yet</h3>
                <p className="text-white/60 mb-6">Find and book your perfect entertainment vendor</p>
                <Link to="/vendors">
                  <Button className="bg-gold text-black hover:bg-gold-light rounded-full font-bold" data-testid="browse-vendors-cta">
                    Browse Vendors
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {bookings.map((booking, index) => (
                  <motion.div
                    key={booking.booking_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-charcoal rounded-xl border border-white/5 p-6"
                    data-testid={`booking-card-${booking.booking_id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-heading text-lg font-bold text-white">
                            {booking.vendor?.business_name}
                          </h3>
                          <Badge className={getStatusBadge(booking.status)} variant="outline">
                            {booking.status}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap items-center gap-4 text-white/60 text-sm">
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 mr-1" />
                            {booking.event_date}
                          </div>
                          <div className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            {booking.event_time} ({booking.duration_hours}hrs)
                          </div>
                          <div className="flex items-center">
                            <DollarSign className="w-4 h-4 mr-1" />
                            ${booking.total_amount}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <Badge className={getPaymentBadge(booking.payment_status)} variant="outline">
                          {booking.payment_status === "service_fee_paid" ? "Confirmed" : booking.payment_status.replace("_", " ")}
                        </Badge>
                        
                        {!booking.contract_signed && (
                          <Link to={`/contract/${booking.booking_id}`}>
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="border-gold/30 text-gold hover:bg-gold/10 rounded-full"
                              data-testid={`sign-contract-${booking.booking_id}`}
                            >
                              <FileText className="w-4 h-4 mr-1" />
                              Sign Contract
                            </Button>
                          </Link>
                        )}
                        
                        {booking.contract_signed && booking.payment_status === "unpaid" && (
                          <Button 
                            size="sm"
                            onClick={() => openPaymentDialog(booking, "service_fee")}
                            className="bg-gold text-black hover:bg-gold-light rounded-full"
                            data-testid={`pay-service-fee-${booking.booking_id}`}
                          >
                            <CreditCard className="w-4 h-4 mr-1" />
                            Pay Service Fee (${(booking.service_fee || Math.max(booking.total_amount * 0.10, 25)).toFixed(2)})
                          </Button>
                        )}
                        
                        {(booking.payment_status === "service_fee_paid" || booking.payment_status === "deposit_paid") && booking.status !== "completed" && (
                          <div className="text-sm text-white/60">
                            <span className="text-green-400">✓ Service fee paid</span>
                            <span className="mx-2">|</span>
                            <span>Balance ${(booking.vendor_balance || booking.total_amount - (booking.service_fee || 25)).toFixed(2)} due to vendor at event</span>
                          </div>
                        )}
                        
                        {/* Review button for completed bookings */}
                        {booking.status === "completed" && !booking.has_review && (
                          <Button 
                            size="sm"
                            onClick={() => openReviewDialog(booking)}
                            className="bg-gold text-black hover:bg-gold-light rounded-full"
                            data-testid={`write-review-${booking.booking_id}`}
                          >
                            <Star className="w-4 h-4 mr-1" />
                            Write Review
                          </Button>
                        )}
                        
                        {booking.status === "completed" && booking.has_review && (
                          <Badge className="bg-green-500/10 text-green-400 border-green-500/20" variant="outline">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Reviewed
                          </Badge>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="messages">
            <div className="grid lg:grid-cols-3 gap-6 h-[600px]">
              {/* Conversations List */}
              <div className="bg-charcoal rounded-xl border border-white/5 overflow-hidden">
                <div className="p-4 border-b border-white/5">
                  <h3 className="font-heading font-bold text-white">Conversations</h3>
                </div>
                <ScrollArea className="h-[520px]">
                  {conversations.length === 0 ? (
                    <div className="p-6 text-center">
                      <MessageCircle className="w-8 h-8 text-gold/30 mx-auto mb-2" />
                      <p className="text-white/60 text-sm">No conversations yet</p>
                    </div>
                  ) : (
                    conversations.map((conv) => (
                      <button
                        key={conv.conversation_id}
                        onClick={() => handleConversationSelect(conv)}
                        className={`w-full p-4 text-left hover:bg-white/5 transition-colors border-b border-white/5 ${
                          selectedConversation?.conversation_id === conv.conversation_id ? "bg-white/5" : ""
                        }`}
                        data-testid={`conversation-${conv.conversation_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center">
                            <User className="w-5 h-5 text-gold" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-white truncate">
                              {conv.other_user?.name || "Unknown"}
                            </p>
                            <p className="text-sm text-white/40 truncate">
                              {conv.last_message}
                            </p>
                          </div>
                          {conv.unread_count > 0 && (
                            <span className="w-5 h-5 rounded-full bg-gold text-black text-xs flex items-center justify-center font-bold">
                              {conv.unread_count}
                            </span>
                          )}
                        </div>
                      </button>
                    ))
                  )}
                </ScrollArea>
              </div>

              {/* Messages */}
              <div className="lg:col-span-2 bg-charcoal rounded-xl border border-white/5 overflow-hidden flex flex-col">
                {selectedConversation ? (
                  <>
                    <div className="p-4 border-b border-white/5">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center">
                          <User className="w-5 h-5 text-gold" />
                        </div>
                        <div>
                          <p className="font-medium text-white">{selectedConversation.other_user?.name}</p>
                          <p className="text-sm text-white/40">{selectedConversation.other_user?.email}</p>
                        </div>
                      </div>
                    </div>
                    
                    <ScrollArea className="flex-1 p-4">
                      <div className="space-y-4">
                        {messages.map((msg) => (
                          <div
                            key={msg.message_id}
                            className={`flex ${msg.sender_id === user?.user_id ? "justify-end" : "justify-start"}`}
                          >
                            <div
                              className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                                msg.sender_id === user?.user_id
                                  ? "bg-gold text-black"
                                  : "bg-obsidian text-white"
                              }`}
                            >
                              <p>{msg.content}</p>
                              <p className={`text-xs mt-1 ${msg.sender_id === user?.user_id ? "text-black/60" : "text-white/40"}`}>
                                {new Date(msg.created_at).toLocaleTimeString()}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                    
                    <form onSubmit={handleSendMessage} className="p-4 border-t border-white/5">
                      <div className="flex gap-2">
                        <Input
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          placeholder="Type a message..."
                          className="flex-1 bg-obsidian border-white/10 text-white rounded-full"
                          data-testid="message-input"
                        />
                        <Button 
                          type="submit" 
                          className="bg-gold text-black hover:bg-gold-light rounded-full"
                          data-testid="send-message-btn"
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                    </form>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                      <MessageCircle className="w-12 h-12 text-gold/30 mx-auto mb-4" />
                      <p className="text-white/60">Select a conversation to start messaging</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Payment Method Dialog */}
        <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
          <DialogContent className="bg-charcoal border-white/10 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Choose Payment Method</DialogTitle>
            </DialogHeader>
            
            {selectedBookingForPayment && (
              <div className="space-y-4 mt-4">
                <div className="bg-obsidian rounded-xl p-4 border border-white/5">
                  <div className="flex justify-between items-center">
                    <span className="text-white/60">Amount to Pay</span>
                    <span className="font-heading text-2xl font-bold text-gold">
                      ${selectedPaymentType === "deposit" 
                        ? selectedBookingForPayment.deposit_amount 
                        : (selectedBookingForPayment.total_amount - selectedBookingForPayment.deposit_amount)}
                    </span>
                  </div>
                  <p className="text-white/40 text-sm mt-1">
                    {selectedPaymentType === "deposit" ? "25% Deposit" : "Remaining Balance"}
                  </p>
                </div>
                
                <div className="space-y-3">
                  <Button
                    onClick={() => handlePayment(selectedBookingForPayment.booking_id, selectedPaymentType, "stripe")}
                    className="w-full h-14 bg-[#635BFF] hover:bg-[#5851DB] text-white rounded-xl font-medium"
                    data-testid="pay-with-stripe"
                  >
                    <CreditCard className="w-5 h-5 mr-3" />
                    Pay with Card
                  </Button>
                </div>
                
                <p className="text-white/40 text-xs text-center">
                  All payments are secure and encrypted
                </p>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Review Dialog */}
        <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
          <DialogContent className="bg-charcoal border-white/10 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <Star className="w-5 h-5 text-gold" />
                Write a Review
              </DialogTitle>
            </DialogHeader>
            
            {selectedBookingForReview && (
              <div className="space-y-6 mt-4">
                <div className="bg-obsidian rounded-xl p-4 border border-white/5">
                  <p className="text-white font-medium">{selectedBookingForReview.vendor?.business_name}</p>
                  <p className="text-white/60 text-sm">
                    {selectedBookingForReview.event_type} on {selectedBookingForReview.event_date}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-white">Your Rating</Label>
                  <StarRating 
                    rating={reviewForm.rating} 
                    onRate={(r) => setReviewForm(prev => ({ ...prev, rating: r }))}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-white">Review Title (Optional)</Label>
                  <Input
                    value={reviewForm.title}
                    onChange={(e) => setReviewForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Summarize your experience"
                    className="bg-obsidian border-white/10 text-white"
                    maxLength={100}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-white">Your Review</Label>
                  <Textarea
                    value={reviewForm.comment}
                    onChange={(e) => setReviewForm(prev => ({ ...prev, comment: e.target.value }))}
                    placeholder="Tell others about your experience with this vendor..."
                    className="bg-obsidian border-white/10 text-white min-h-[120px]"
                    maxLength={1000}
                  />
                  <p className="text-white/40 text-xs text-right">
                    {reviewForm.comment.length}/1000 characters
                  </p>
                </div>
                
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowReviewDialog(false)}
                    className="flex-1 border-white/10 text-white hover:bg-white/5 rounded-full"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmitReview}
                    disabled={submittingReview || reviewForm.comment.length < 10}
                    className="flex-1 bg-gold text-black hover:bg-gold-light rounded-full"
                  >
                    {submittingReview ? "Submitting..." : "Submit Review"}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default CustomerDashboard;
